import { useMutation, useQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { useState } from 'react';
import styled from 'styled-components';
import {
  GQL_ADD_TO_FRIEND_LIST,
  GQL_FRIEND_LIST,
  GQL_REMOBE_FROM_FRIEND_LIST,
  StoredFriends,
} from '~/gql/friends-list/friends-list';
import { authClient } from '~/helpers/apollo';
import { formatDate, renderFullPath } from '~/utils/common/utils';
import iconStar from '@assets/images/icon/icon-star-gold.png';
import iconStarDisabled from '@assets/images/icon/icon-friend-add.png';
import iconFriendHover from '@assets/images/icon/icon-friend-add-hover.png';
import { WonBattleItem } from '~/components/ui/won-battle-item';
import { SSpanTableAdvice, SSpanTableName } from '~/components/styles/styles';
import ModalListAuto from '~/components/ui/modal-list-auto';
import { WrapperListAuto } from '~/components/ui/wrapper-list-auto';
import { Loading } from '~/components/ui/loading';

interface IProps {
  open: boolean;
  onClose: () => void;
}
type TProps = IProps;

export default function FriendsdDialog({ open, onClose }: TProps) {
  const { i18n } = useLingui();

  const [friends, setFriends] = useState<StoredFriends[]>([]);

  const { refetch: fetchFriends, loading } = useQuery(GQL_FRIEND_LIST, {
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setFriends(data.getFriendsList);
    },
  });

  const [addFriendToList] = useMutation(GQL_ADD_TO_FRIEND_LIST, {
    client: authClient,
    onCompleted: () => {
      getFriendsList();
    },
  });

  const [removeFriendToList] = useMutation(GQL_REMOBE_FROM_FRIEND_LIST, {
    client: authClient,
    onCompleted: () => {
      getFriendsList();
    },
  });

  async function getFriendsList() {
    await fetchFriends();
  }

  async function handleAdd(id: string) {
    await addFriendToList({
      variables: {
        friendId: id,
      },
    });
  }

  async function handleRemove(id: string) {
    await removeFriendToList({
      variables: {
        friendId: id,
      },
    });
  }

  return (
    <ModalListAuto onClose={onClose} open={open}>
      <WrapperListAuto onClose={onClose} title={`${i18n._('common.friends')}`}>
        {loading ? (
          <Loading />
        ) : (
          <>
            <STableWrapper>
              <STable>
                <tbody>
                  {friends.map((user, index) => (
                    <STr
                      key={index}
                      onClick={
                        user.isFriend
                          ? () => handleRemove(user.id)
                          : () => handleAdd(user.id)
                      }
                    >
                      <td>
                        <WonBattleItem wonBattles={user.wonBattles} />
                      </td>
                      <td>
                        <SDivNameWrapper>
                          {user.avatar?.fileName && (
                            <SDivAvatar>
                              <img
                                src={renderFullPath(user.avatar?.fileName)}
                                alt=""
                              />
                            </SDivAvatar>
                          )}
                          <SDivName>
                            <SSpanTableName>{user.username}</SSpanTableName>
                            <SSpanTableAdvice className="hidden">
                              {user.isFriend
                                ? `ajouté le ${formatDate(user.createdAt as Date)}`
                                : 'ajouter en ami'}
                            </SSpanTableAdvice>
                          </SDivName>
                        </SDivNameWrapper>
                      </td>
                      <td></td>
                      <td>
                        <SDivFriend $isFriend={user.isFriend} />
                      </td>
                    </STr>
                  ))}
                </tbody>
              </STable>
            </STableWrapper>
          </>
        )}
      </WrapperListAuto>
    </ModalListAuto>
  );
}

const SDivName = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const SDivNameWrapper = styled.div`
  background-color: transparent;
  height: 44px;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SDivFriend = styled.button<{ $isFriend?: boolean }>`
  background-color: transparent;
  background: ${({ $isFriend }) => {
    if ($isFriend) return `url(${iconStar}) center/contain no-repeat`;
    return `url(${iconStarDisabled}) center/contain no-repeat`;
  }};
  height: 44px;
  width: 100%;
  position: relative;
  outline: none;
  border: none;

  &:hover {
    background: ${({ $isFriend }) => {
      if ($isFriend) return `url(${iconStar}) center/contain no-repeat`;
      return `url(${iconFriendHover}) center/contain no-repeat`;
    }};
    outline: none;
    border: none;
    transform: scale(1.1);
  }
  &:focus {
    outline: none;
    border: none;
    transform: scale(1.1);
  }

  img {
    position: absolute;
    object-fit: contain;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const SDivAvatar = styled.div`
  border-radius: 50%;
  background-color: #fff;
  height: 40px;
  width: 44px;
  position: relative;
  border: 3px solid #fff;
  margin-right: 20px;

  img {
    position: absolute;
    object-fit: contain;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const STableWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 20px 20px 130px 20px;

  @media screen and (max-height: 800px) {
    padding: 10px 10px 130px 40px;
  }

  @media screen and (max-height: 450px) {
    padding: 10px 10px 100px 40px;
  }

  @media screen and (max-width: 450px) {
    padding: 10px 10px 100px 10px;
  }
`;

const STr = styled.tr<{ $isFriend?: boolean }>`
  &:nth-of-type(even) {
    /* background-color: #ffffff80; */
    user-select: none;
  }
  &:nth-of-type(odd) {
    background-color: transparent;
    user-select: none;
  }
  td {
    color: #113969;
    font-family: 'Riffic Free', sans-serif;
    background-color: transparent;
    font-weight: bold;
    font-size: 1.1rem;
    padding: 8px 12px;
    user-select: none;

    @media screen and (max-width: 450px) {
      font-size: 0.9rem;
      padding: 5px 8px;
    }
    @media screen and (max-width: 290px) {
      font-size: 0.8rem;
      padding: 5px 8px;
    }
    @media screen and (max-height: 600px) {
      font-size: 0.9rem;
      padding: 5px 8px;
    }
    @media screen and (max-height: 450px) {
      font-size: 0.8rem;
      padding: 5px 8px;
    }
    @media screen and (max-height: 290px) {
      font-size: 0.7rem;
      padding: 5px 8px;
    }

    &:first-of-type {
      text-align: center;
      width: 10%;
      user-select: none;
    }
    &:nth-of-type(2) {
      width: 75%;
      user-select: none;
    }
    &:last-of-type {
      padding-right: 10px;
      text-align: right;
      width: 15%;
      user-select: none;
    }
  }

  & > td > div > div > span {
    transition: all 0.5s linear;
    &.hidden {
      visibility: hidden;
      height: 0;
      opacity: 0;
    }
  }

  &:hover {
    background-color: rgba(83, 210, 215, 0.2);
    & > td > div > div > span {
      &.hidden {
        visibility: visible;
        height: auto;
        opacity: 1;
      }
    }
    & > td > button {
      background: ${({ $isFriend }) => {
        if ($isFriend) return `url(${iconStar}) center/contain no-repeat`;
        return `url(${iconFriendHover}) center/contain no-repeat`;
      }};
    }
  }
`;

const STable = styled.table`
  text-align: left;
  position: relative;
  border-collapse: collapse;
  width: 100%;

  tbody {
    tr {
    }
  }
`;
